import React, { useState, useEffect } from "react";
import { Table, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import { getRequest, patchRequest } from "services/api";
import { useCategory } from "../../context/CategoryContext";

const BulkUpdateTax = ({ onSuccess, onError }) => {
  const { categories } = useCategory();
  
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Filtering & Search
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  
  // Selection and values
  const [checkedIds, setCheckedIds] = useState({});
  const [taxValues, setTaxValues] = useState({});
  const [globalTax, setGlobalTax] = useState("");

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const response = await getRequest("/menu?paginate=1000&include=categories");
      if (response && response.success) {
        const menuData = response.data || [];
        setMenus(menuData);
        
        // Initialize tax values
        const initialTaxes = {};
        menuData.forEach((menu) => {
          initialTaxes[menu.id] = menu.tax_percent !== null ? menu.tax_percent : 0;
        });
        setTaxValues(initialTaxes);
      } else {
        onError("Failed to fetch menus");
      }
    } catch (err) {
      console.error(err);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered menus based on search and category
  const filteredMenus = menus.filter((menu) => {
    const matchesSearch = menu.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      !selectedCategory ||
      (menu.categories && menu.categories.some((cat) => cat.id === selectedCategory));
    return matchesSearch && matchesCategory;
  });

  const handleCheckboxChange = (id) => {
    setCheckedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    const newCheckedIds = { ...checkedIds };
    filteredMenus.forEach((menu) => {
      newCheckedIds[menu.id] = isChecked;
    });
    setCheckedIds(newCheckedIds);
  };

  const handleTaxChange = (id, val) => {
    // Keep it as a positive number or empty/0
    const intVal = val === "" ? "" : Math.max(0, parseInt(val, 10) || 0);
    setTaxValues((prev) => ({
      ...prev,
      [id]: intVal,
    }));
  };

  const handleApplyGlobalTax = () => {
    if (globalTax === "") return;
    const taxNum = Math.max(0, parseInt(globalTax, 10) || 0);
    const newTaxValues = { ...taxValues };
    
    // Apply only to checked menus
    Object.keys(checkedIds).forEach((id) => {
      if (checkedIds[id]) {
        newTaxValues[id] = taxNum;
      }
    });
    
    setTaxValues(newTaxValues);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get array of selected menus
    const selectedMenus = menus
      .filter((menu) => checkedIds[menu.id])
      .map((menu) => ({
        id: menu.id,
        tax_percent: taxValues[menu.id] !== "" ? taxValues[menu.id] : 0,
      }));

    if (selectedMenus.length === 0) {
      onError("Please select at least one menu to update");
      return;
    }

    setSubmitting(true);
    try {
      const response = await patchRequest("/menu/bulk-update-tax", {
        menus: selectedMenus,
      });

      if (response && response.success) {
        onSuccess();
      } else {
        onError(response?.message || "Failed to update tax");
      }
    } catch (err) {
      console.error(err);
      onError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Determine if all filtered menus are selected
  const allFilteredChecked =
    filteredMenus.length > 0 &&
    filteredMenus.every((menu) => checkedIds[menu.id]);

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: "80vh" }}>
      {/* Search & Category Filter Controls */}
      <Row className="mb-3 g-2">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search product name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md={6}>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Global Apply Tax Control */}
      <div className="p-3 mb-3 bg-light border rounded">
        <Row className="align-items-center g-2">
          <Col xs={12} sm={6} md={7}>
            <Form.Group className="mb-0">
              <Form.Label className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
                Set Global Tax to Checked Items
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter tax percentage (e.g. 11)"
                value={globalTax}
                min="0"
                max="100"
                onChange={(e) => setGlobalTax(e.target.value)}
                style={{ height: "38px" }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={5} className="d-grid mt-auto">
            <Button
              variant="outline-primary"
              onClick={handleApplyGlobalTax}
              disabled={globalTax === "" || filteredMenus.length === 0}
              style={{ height: "38px" }}
            >
              Apply to Checked
            </Button>
          </Col>
        </Row>
      </div>

      {/* Product List Table */}
      <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: "400px", border: "1px solid #dee2e6", borderRadius: "4px" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <Spinner animation="border" variant="primary" />
            <span className="ms-2">Loading menus...</span>
          </div>
        ) : (
          <Table striped hover size="sm" className="mb-0 align-middle">
            <thead className="bg-white sticky-top" style={{ zIndex: 1 }}>
              <tr>
                <th style={{ width: "40px" }} className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={allFilteredChecked}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Menu</th>
                <th style={{ width: "120px" }}>Current Tax</th>
                <th style={{ width: "120px" }}>Target Tax (%)</th>
              </tr>
            </thead>
            <tbody>
              {filteredMenus.length > 0 ? (
                filteredMenus.map((menu) => (
                  <tr key={menu.id} className={checkedIds[menu.id] ? "table-primary-lt" : ""}>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={!!checkedIds[menu.id]}
                        onChange={() => handleCheckboxChange(menu.id)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={menu.menu_url || "/images/default-product.png"}
                          alt={menu.name}
                          style={{
                            width: "35px",
                            height: "35px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            marginRight: "10px",
                          }}
                        />
                        <div>
                          <div className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
                            {menu.name}
                          </div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            {menu.categories?.map((c) => c.name).join(", ") || "No Category"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-secondary-lt">
                        {menu.tax_percent !== null ? `${menu.tax_percent}%` : "0%"}
                      </span>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        size="sm"
                        min="0"
                        max="100"
                        value={taxValues[menu.id] ?? ""}
                        onChange={(e) => handleTaxChange(menu.id, e.target.value)}
                        disabled={!checkedIds[menu.id]}
                        style={{ height: "30px", fontSize: "13px" }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-4 text-muted">
                    No menus found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Action Footer */}
      <div className="mt-auto pt-3 border-top d-flex justify-content-end gap-2">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || loading}
          className="px-4"
        >
          {submitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Updating...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};

export default BulkUpdateTax;
